
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { processResources } from "./useResourceProcessing";
import { processInvitations } from "./useInvitationProcessing";
import { ResourceWithFiles } from "./types";

export const useResources = (userId?: string) => {
  const { toast } = useToast();
  const { data: profile, isLoading: profileLoading } = useProfile(userId);
  
  return useQuery({
    queryKey: ['resources', userId, profile?.role],
    queryFn: async () => {
      if (!userId) {
        console.log('No userId provided');
        return [];
      }

      if (profile?.role === 'admin') {
        console.log('Fetching all resources as admin');
        const { data, error } = await supabase
          .from('resources')
          .select(`
            *,
            resource_files (
              id,
              title,
              description,
              file_url,
              order_index,
              file_type,
              quiz_id,
              passing_score,
              quiz_description
            )
          `)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching resources:', error);
          toast({
            title: "Error",
            description: "Failed to load resources",
            variant: "destructive",
          });
          throw error;
        }

        return await processResources(data as ResourceWithFiles[]);
      } else {
        console.log('Fetching shared resources for trainee:', userId);
        // First check if user has any direct shares
        const { data: sharedResources, error } = await supabase
          .from('resource_shares')
          .select(`
            resource:resources (
              id,
              topic,
              company,
              status,
              preview_image_url,
              created_at,
              resource_files (
                id,
                title,
                description,
                file_url,
                order_index,
                file_type,
                quiz_id,
                passing_score,
                quiz_description
              )
            )
          `)
          .eq('trainee_id', userId)
          .eq('status', 'accepted');

        if (error) {
          console.error('Error fetching shared resources:', error);
          toast({
            title: "Error",
            description: "Failed to load shared resources",
            variant: "destructive",
          });
          throw error;
        }

        console.log('Shared resources result:', sharedResources);

        // Also look for any pending invitations by email if there's a profile
        let invitationData: any[] = [];
        if (profile?.email) {
          const { data: invites, error: invitationError } = await supabase
            .from('resource_invitations')
            .select(`
              resource:resources (
                id,
                topic,
                company,
                status,
                preview_image_url,
                created_at,
                resource_files (
                  id,
                  title,
                  description,
                  file_url,
                  order_index,
                  file_type,
                  quiz_id,
                  passing_score,
                  quiz_description
                )
              )
            `)
            .eq('email', profile.email)
            .eq('status', 'pending');

          if (invitationError) {
            console.error('Error fetching resource invitations:', invitationError);
          } else if (invites && invites.length > 0) {
            console.log('Found pending invitations:', invites);
            invitationData = invites;
            
            // Auto-accept pending invitations
            await processInvitations(profile.email, userId, invites);
            
            // Add the invited resources to the shared resources
            sharedResources.push(...invites);
          }
        }

        // Process resources to fetch quiz questions for any quiz files
        const processedResources = await processResources(
          sharedResources
            .map(share => share.resource)
            .filter((resource): resource is NonNullable<typeof resource> => resource !== null) as ResourceWithFiles[]
        );

        return processedResources;
      }
    },
    enabled: !!userId && !profileLoading,
    retry: 2
  });
};
